import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search, Plus, Filter, ArrowUpDown, Settings2, X } from 'lucide-react';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { ProjectCreationModal } from '@/components/project-creation-modal';
import { ColumnConfiguratorDrawer, DEFAULT_COLUMNS, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useProjectForm } from '@/providers/ProjectFormProvider';
import { PaginationControls } from '@/components/pagination-controls';
import { fetchProjects, fetchClients, fetchEmployees, fetchTasks, fetchMilestones, fetchResourceAllocations, type ProjectFilterParams } from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getHealthBadgeColor,
  getStatusColor,
  formatStatus,
  getVarianceColor,
  calculateDaysRemaining,
  calculateGrossProfit,
  calculateForecastAtCompletion,
  formatProjectType,
  formatRelativeTime,
  getDaysRemainingColor,
  getProjectTypeColor,
  getWIPColor,
} from '@/lib/utils';
import type { Project, Client, Employee, ProjectStatus, ProjectHealth, Task, Milestone, ResourceAllocation } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';
import { useDisplayDensity } from '@/hooks/use-display-density';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { usePagination } from '@/hooks/use-pagination';
import { useKeyboardPagination } from '@/hooks/use-keyboard-pagination';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { usePersistedFilters } from '@/hooks/use-persisted-filters';
import { ROUTES } from '@/routes';
import { useCompany } from '@/contexts/CompanyContext';

interface ProjectsPageProps {
  onNavigateToProject?: (projectId: number) => void;
}

export function ProjectsPage({ onNavigateToProject }: ProjectsPageProps = {}) {
  const navigate = useNavigate();
  const density = useDisplayDensity();
  const { currentCompany } = useCompany();
  const { hasMinimizedForm, formState, acknowledgeModalOpen, restoreForm } = useProjectForm();
  const { preferences } = useUserPreferences();
  const tableRef = useRef<HTMLDivElement>(null);
  const { handleApiError } = useErrorHandler({ useToast: true });
  
  // Pagination (don't persist to URL to keep URLs clean)
  const {
    currentPage,
    pageSize,
    paginationParams,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });
  
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata>({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    startIndex: 0,
    endIndex: 0,
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  
  // Persisted filters - will be restored when navigating back to this page
  const { filters, updateFilter, resetFilters } = usePersistedFilters({
    storageKey: 'projects-list-filters',
    defaultFilters: {
      searchTerm: '',
      statusFilter: 'all' as ProjectStatus | 'all',
      healthFilter: 'all' as ProjectHealth | 'all',
      sortBy: 'health' as 'name' | 'health' | 'value' | 'completion',
    },
  });
  
  const { searchTerm, statusFilter, healthFilter, sortBy } = filters;
  
  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || healthFilter !== 'all';
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  
    // Use column preferences hook for persistence (API in live mode, localStorage in demo mode)
    const { columns, updateColumns, isLoading: columnsLoading } = useColumnPreferences({
      storageKey: 'projects-column-preferences',
      defaultColumns: DEFAULT_COLUMNS,
    });

  // Load reference data when component mounts or company changes
  useEffect(() => {
    async function loadReferenceData() {
      try {
        const [clientsData, employeesData, tasksData, milestonesData, allocationsData] = await Promise.all([
          fetchClients('projects'),
          fetchEmployees('projects'),
          fetchTasks('projects'),
          fetchMilestones('projects'),
          fetchResourceAllocations('projects'),
        ]);
        setClients(clientsData);
        setEmployees(employeesData);
        setTasks(tasksData);
        setMilestones(milestonesData);
        setResourceAllocations(allocationsData);
      } catch (error) {
        handleApiError(error, '/api/reference-data', 'GET');
      }
    }
    loadReferenceData();
  }, [handleApiError, currentCompany?.id]);

  // Open modal when form should be restored
  useEffect(() => {
    if (formState?.shouldOpenModal) {
      setCreateModalOpen(true);
      acknowledgeModalOpen();
    }
  }, [formState?.shouldOpenModal, acknowledgeModalOpen]);

  // Debounced search term for API calls (to avoid too many requests while typing)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to load/refresh projects with pagination and filters
  const loadProjects = async (
    page: number = currentPage, 
    size: number = pageSize,
    filters?: ProjectFilterParams
  ) => {
    // Show loading state for initial load, transition state for page changes
    if (projects.length === 0) {
      setLoading(true);
    } else {
      setIsTransitioning(true);
    }

    try {
      // Build filter params
      const filterParams: ProjectFilterParams = filters || {
        status: statusFilter,
        health: healthFilter,
        search: debouncedSearchTerm,
        sortBy: sortBy,
      };

      // Fetch projects with pagination and filter params
      const result = await fetchProjects('projects', { page, pageSize: size }, filterParams);
      
      // Check if result is a paginated response
      if (result && typeof result === 'object' && 'data' in result && 'pagination' in result) {
        // Server returned paginated response
        setProjects(result.data);
        setPagination({
          currentPage: result.pagination.currentPage,
          pageSize: result.pagination.pageSize,
          totalItems: result.pagination.totalItems,
          totalPages: result.pagination.totalPages,
          hasNextPage: result.pagination.hasNextPage,
          hasPreviousPage: result.pagination.hasPreviousPage,
          startIndex: result.pagination.startIndex,
          endIndex: result.pagination.endIndex,
        });
      } else {
        // Fallback: result is an array (demo mode or non-paginated response)
        const projectsArray = Array.isArray(result) ? result : [];
        setProjects(projectsArray);
        
        // Calculate pagination client-side for demo mode
        const totalItems = projectsArray.length;
        const totalPages = Math.ceil(totalItems / size);
        const safePage = Math.max(1, Math.min(page, totalPages || 1));
        const startIndex = (safePage - 1) * size;
        const endIndex = Math.min(startIndex + size, totalItems);
        
        setPagination({
          currentPage: safePage,
          pageSize: size,
          totalItems,
          totalPages,
          hasNextPage: safePage < totalPages,
          hasPreviousPage: safePage > 1,
          startIndex,
          endIndex,
        });
      }
      setLoading(false);
    } catch (error) {
      handleApiError(error, '/api/projects', 'GET');
      setLoading(false);
    } finally {
      setIsTransitioning(false);
    }
  };

  // Load projects when page, pageSize, or filters change
  useEffect(() => {
    loadProjects(currentPage, pageSize, {
      status: statusFilter,
      health: healthFilter,
      search: debouncedSearchTerm,
      sortBy: sortBy,
    });
  }, [currentPage, pageSize, statusFilter, healthFilter, debouncedSearchTerm, sortBy, currentCompany?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 when filters change (but not when page/pageSize changes)
  const prevFiltersRef = useRef({ statusFilter, healthFilter, debouncedSearchTerm, sortBy });
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged = 
      prevFilters.statusFilter !== statusFilter ||
      prevFilters.healthFilter !== healthFilter ||
      prevFilters.debouncedSearchTerm !== debouncedSearchTerm ||
      prevFilters.sortBy !== sortBy;
    
    if (filtersChanged && currentPage !== 1) {
      resetPagination();
    }
    
    prevFiltersRef.current = { statusFilter, healthFilter, debouncedSearchTerm, sortBy };
  }, [statusFilter, healthFilter, debouncedSearchTerm, sortBy, currentPage, resetPagination]);

  // Track the previous page to detect actual page changes
  const prevPageRef = useRef(currentPage);

  // Scroll to top when page changes (but not on initial load or filter changes)
  useEffect(() => {
    // Only scroll if the page number actually changed (not on initial mount or filter changes)
    if (prevPageRef.current !== currentPage && prevPageRef.current !== undefined) {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    prevPageRef.current = currentPage;
  }, [currentPage]);

  // Keyboard shortcuts for pagination
  useKeyboardPagination({
    currentPage,
    totalPages: pagination.totalPages,
    onPageChange: setPage,
    enabled: !loading && !createModalOpen && !columnConfigOpen,
  });

  // Get current page of projects
  // Projects are now filtered and sorted by the server/data-service
  // The projects state already contains the current page data
  const paginatedProjects = useMemo(() => {
    return projects;
  }, [projects]);

  // Get client names - now supports multiple clients via clientNames field
  const getClientNames = (project: Project) => {
    // If project has clientNames populated (from stored procedure), use it directly
    if (project.clientNames) {
      return project.clientNames;
    }
    // Fallback: look up client names from clientIds
    if (project.clientIds) {
      const ids = project.clientIds.split(',').filter(Boolean);
      const names = ids.map(id => clients.find(c => c.id === parseInt(id, 10))?.name || 'Unknown');
      return names.join(', ');
    }
    return 'Unknown';
  };

  const getManagerName = (managerId: number) => {
    return employees.find(e => e.id === managerId)?.name || 'Unknown';
  };

    const handleSaveColumns = (newColumns: ColumnDefinition[]) => {
      updateColumns(newColumns);
    };

  // Get visible columns sorted by order
  const visibleColumns = columns
    .filter(col => col.visible)
    .sort((a, b) => a.order - b.order);

  // Helper functions for calculated fields
  const getOverdueTasks = (projectId: number): number => {
    const now = new Date();
    return tasks.filter(
      t => t.projectId === projectId && 
      t.status !== 'completed' && 
      new Date(t.dueDate) < now
    ).length;
  };

  const getOverdueMilestones = (projectId: number): number => {
    return milestones.filter(
      m => m.projectId === projectId && m.status === 'overdue'
    ).length;
  };

  const getNextMilestone = (projectId: number): Milestone | undefined => {
    const now = new Date();
    const upcoming = milestones
      .filter(m => m.projectId === projectId && m.status === 'pending')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    return upcoming[0];
  };

  const getAllocatedResources = (projectId: number): number => {
    const uniqueEmployees = new Set(
      resourceAllocations
        .filter(a => a.projectId === projectId)
        .map(a => a.employeeId)
    );
    return uniqueEmployees.size;
  };

  const getResourceConflicts = (projectId: number): number => {
    return resourceAllocations.filter(
      a => a.projectId === projectId && a.conflictLevel !== 'none'
    ).length;
  };

  // Get the first client for a project (for single-value fields like contact, industry)
  const getFirstClient = (project: Project): Client | undefined => {
    if (project.clientIds) {
      const firstId = parseInt(project.clientIds.split(',')[0], 10);
      return clients.find(c => c.id === firstId);
    }
    return undefined;
  };

  // Get client contact name(s) for a project
  const getClientContacts = (project: Project): string => {
    if (project.clientIds) {
      const ids = project.clientIds.split(',').filter(Boolean);
      const contacts = ids
        .map(id => clients.find(c => c.id === parseInt(id, 10))?.contactName)
        .filter(Boolean);
      return contacts.length > 0 ? contacts.join(', ') : '-';
    }
    return '-';
  };

  // Get client industry/industries for a project
  const getClientIndustries = (project: Project): string => {
    if (project.clientIds) {
      const ids = project.clientIds.split(',').filter(Boolean);
      const industries = [...new Set(
        ids
          .map(id => clients.find(c => c.id === parseInt(id, 10))?.industry)
          .filter(Boolean)
      )];
      return industries.length > 0 ? industries.join(', ') : '-';
    }
    return '-';
  };

  // Get completed milestones count for a project
  const getCompletedMilestones = (projectId: number): number => {
    return milestones.filter(
      m => m.projectId === projectId && m.status === 'completed'
    ).length;
  };

  // Get total milestones count for a project
  const getTotalMilestones = (projectId: number): number => {
    return milestones.filter(m => m.projectId === projectId).length;
  };

  // Calculate utilization rate for a project
  const getUtilizationRate = (project: Project): number => {
    if (project.budgetHours === 0) return 0;
    return (project.actualHours / project.budgetHours) * 100;
  };

  // Calculate project duration in days
  const calculateDuration = (startDate: string, endDate: string): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Render column value based on column ID
  const renderColumnValue = (column: ColumnDefinition, project: Project) => {
    const cellClass = column.align === 'right' ? `${density.cell} text-right` : density.cell;

    switch (column.id) {
      case 'project-name':
        return (
          <TableCell key={column.id} className={density.cell}>
            <div className="font-medium">{project.name}</div>
          </TableCell>
        );

      case 'project-code':
        return (
          <TableCell key={column.id} className={density.cell}>
            {project.code || '-'}
          </TableCell>
        );

      case 'client-name':
        return (
          <TableCell key={column.id} className={density.cell}>
            {getClientNames(project)}
          </TableCell>
        );

      case 'client-contact':
        return (
          <TableCell key={column.id} className={density.cell}>
            {getClientContacts(project)}
          </TableCell>
        );

      case 'industry':
        return (
          <TableCell key={column.id} className={density.cell}>
            {getClientIndustries(project)}
          </TableCell>
        );

      case 'account-manager':
        return (
          <TableCell key={column.id} className={density.cell}>
            <span className="text-muted-foreground text-sm">-</span>
          </TableCell>
        );

      case 'project-manager':
        return (
          <TableCell key={column.id} className={density.cell}>
            {getManagerName(project.projectManagerId)}
          </TableCell>
        );

      case 'project-status':
        return (
          <TableCell key={column.id} className={density.cell}>
            <Badge variant="outline" className={getStatusColor(project.status)} disableHover>
              {formatStatus(project.status)}
            </Badge>
          </TableCell>
        );

      case 'health-indicator':
        return (
          <TableCell key={column.id} className={density.cell}>
            <Badge className={getHealthBadgeColor(project.health)} disableHover>
              {project.health.toUpperCase()}
            </Badge>
          </TableCell>
        );

      case 'budget':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
          </TableCell>
        );

      case 'actual-cost':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>
              {formatCurrency(project.actualLabour + project.actualExpense)}
            </SensitiveData>
          </TableCell>
        );

      case 'revenue':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
          </TableCell>
        );

      case 'wip-amount':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>
              <span className={getWIPColor(project.wipAmount)}>
                {formatCurrency(project.wipAmount)}
              </span>
            </SensitiveData>
          </TableCell>
        );

      case 'gross-profit':
        const grossProfit = calculateGrossProfit(
          project.actualRevenue,
          project.actualLabour,
          project.actualExpense
        );
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>
              <span className={grossProfit >= 0 ? 'text-success-600' : 'text-error-600'}>
                {formatCurrency(grossProfit)}
              </span>
            </SensitiveData>
          </TableCell>
        );

      case 'forecast-at-completion':
        const eac = calculateForecastAtCompletion(
          project.actualLabour,
          project.actualExpense,
          project.budgetLabour,
          project.budgetExpense,
          project.completionPercent
        );
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatCurrency(eac)}</SensitiveData>
          </TableCell>
        );

      case 'allocated-hours':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{project.budgetHours.toLocaleString()} hrs</SensitiveData>
          </TableCell>
        );

      case 'variance':
        return (
          <TableCell key={column.id} className={cellClass}>
            <span className={getVarianceColor(project.variancePercent)}>
              {project.variancePercent > 0 ? '+' : ''}
              {formatPercent(project.variancePercent)}
            </span>
          </TableCell>
        );

      case 'completion-percentage':
        return (
          <TableCell key={column.id} className={density.cell}>
            <div className="flex items-center gap-2">
              <Progress value={project.completionPercent} className="h-2 w-20" />
              <span className="text-xs text-muted-foreground w-10 text-right">
                {formatPercent(project.completionPercent, 0)}
              </span>
            </div>
          </TableCell>
        );

      case 'margin':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
          </TableCell>
        );

      case 'project-type':
        return (
          <TableCell key={column.id} className={density.cell}>
            <Badge variant="outline" className={getProjectTypeColor(project.type)} disableHover>
              {formatProjectType(project.type)}
            </Badge>
          </TableCell>
        );

      case 'phase':
        return (
          <TableCell key={column.id} className={density.cell}>
            <span className="text-muted-foreground text-sm">-</span>
          </TableCell>
        );

      case 'start-date':
        return (
          <TableCell key={column.id} className={density.cell}>
            {formatDate(project.startDate)}
          </TableCell>
        );

      case 'end-date':
        return (
          <TableCell key={column.id} className={density.cell}>
            {formatDate(project.endDate)}
          </TableCell>
        );

      case 'days-remaining':
        const daysRemaining = calculateDaysRemaining(project.endDate);
        return (
          <TableCell key={column.id} className={cellClass}>
            {daysRemaining !== null ? (
              <span className={getDaysRemainingColor(daysRemaining)}>
                {daysRemaining > 0 ? `${daysRemaining} days` : daysRemaining === 0 ? 'Today' : 'Overdue'}
              </span>
            ) : (
              <span className="text-muted-foreground text-sm">-</span>
            )}
          </TableCell>
        );

      case 'next-milestone':
        const nextMilestone = getNextMilestone(project.id);
        return (
          <TableCell key={column.id} className={density.cell}>
            {nextMilestone ? (
              <div>
                <div className="font-medium text-sm">{nextMilestone.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(nextMilestone.dueDate)}
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground text-sm">-</span>
            )}
          </TableCell>
        );

      case 'team-members':
        const teamCount = getAllocatedResources(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {teamCount > 0 ? `${teamCount} member${teamCount !== 1 ? 's' : ''}` : '-'}
          </TableCell>
        );

      case 'resource-conflicts':
        const conflicts = getResourceConflicts(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {conflicts > 0 ? (
              <Badge variant="destructive" disableHover>{conflicts}</Badge>
            ) : (
              <span className="text-muted-foreground text-sm">None</span>
            )}
          </TableCell>
        );

      case 'tasks-overdue':
        const overdueTasks = getOverdueTasks(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {overdueTasks > 0 ? (
              <Badge variant="destructive" disableHover>{overdueTasks}</Badge>
            ) : (
              <span className="text-success-600 text-sm">0</span>
            )}
          </TableCell>
        );

      case 'overdue-milestones':
        const overdueMilestones = getOverdueMilestones(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {overdueMilestones > 0 ? (
              <Badge variant="destructive" disableHover>{overdueMilestones}</Badge>
            ) : (
              <span className="text-success-600 text-sm">0</span>
            )}
          </TableCell>
        );

      case 'last-updated':
        return (
          <TableCell key={column.id} className={density.cell}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-sm">{formatRelativeTime(project.updatedAt)}</span>
                </TooltipTrigger>
                <TooltipContent>
                  {formatDate(project.updatedAt)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );

      case 'milestones-completed':
        const completedMilestones = getCompletedMilestones(project.id);
        const totalMilestones = getTotalMilestones(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {totalMilestones > 0 ? (
              <span>{completedMilestones} / {totalMilestones}</span>
            ) : (
              <span className="text-muted-foreground text-sm">-</span>
            )}
          </TableCell>
        );

      case 'profit-margin':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
          </TableCell>
        );

      case 'billing-status':
        return (
          <TableCell key={column.id} className={density.cell}>
            <span className="text-muted-foreground text-sm">-</span>
          </TableCell>
        );

      case 'utilization-rate':
        const utilizationRate = getUtilizationRate(project);
        return (
          <TableCell key={column.id} className={cellClass}>
            <span className={utilizationRate > 100 ? 'text-red-600' : utilizationRate > 80 ? 'text-amber-600' : 'text-green-600'}>
              {formatPercent(utilizationRate)}
            </span>
          </TableCell>
        );

      case 'duration':
        const duration = calculateDuration(project.startDate, project.endDate);
        return (
          <TableCell key={column.id} className={cellClass}>
            {duration} day{duration !== 1 ? 's' : ''}
          </TableCell>
        );

      case 'risk-level':
        return (
          <TableCell key={column.id} className={density.cell}>
            <span className="text-muted-foreground text-sm">-</span>
          </TableCell>
        );

      case 'quality-score':
        return (
          <TableCell key={column.id} className={density.cell}>
            <span className="text-muted-foreground text-sm">-</span>
          </TableCell>
        );

      default:
        return (
          <TableCell key={column.id} className={density.cell}>
            -
          </TableCell>
        );
    }
  };

  const handleNewProjectClick = () => {
    if (hasMinimizedForm) {
      restoreForm();
    }
    setCreateModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Projects"
        subtitle="Manage and monitor all your projects"
        size={20}
        showActions={true}
        actions={
          <Button 
            onClick={handleNewProjectClick}
            variant={hasMinimizedForm ? "info" : "default"}
          >
            <Plus className="h-4 w-4 mr-2" />
            {hasMinimizedForm ? 'Resume Project' : 'New Project'}
          </Button>
        }
      />

      {/* Data Sensitivity Toggle moved to header bar */}

      {/* Projects Table */}
      <Card ref={tableRef}>
        <CardContent className="space-y-4 pt-6">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by project code, name or client..."
                  value={searchTerm}
                  onChange={e => updateFilter('searchTerm', e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => updateFilter('searchTerm', '')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(v) => updateFilter('statusFilter', v as ProjectStatus | 'all')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="not-started">Not Started</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={healthFilter} onValueChange={(v) => updateFilter('healthFilter', v as ProjectHealth | 'all')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Health" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Health</SelectItem>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="amber">Amber</SelectItem>
                <SelectItem value="red">Red</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={resetFilters}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {/* Advanced Filters - to be implemented */}}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
            <Button
              variant="outline"
              onClick={() => setColumnConfigOpen(true)}
            >
              <Settings2 className="h-4 w-4 mr-2" />
              Configure
            </Button>
          </div>

          <div className={`relative overflow-auto max-h-[calc(100vh-320px)] ${isTransitioning ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}`}>
            <Table data-tour="project-list-table" disableScroll>
              <TableHeader className="sticky top-0 z-10 bg-card after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-border">
                <TableRow className={density.row}>
                  {visibleColumns.map(column => (
                    <TableHead 
                      key={column.id} 
                      className={`${column.align === 'right' ? `${density.header} text-right` : density.header} bg-card`}
                    >
                      {column.label || column.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  // Skeleton loading state
                  Array.from({ length: pageSize }).map((_, i) => (
                    <TableRow key={i} className={density.row}>
                      {visibleColumns.map(column => (
                        <TableCell key={column.id} className={density.cell}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : pagination.totalItems === 0 ? (
                  <TableRow className={density.row}>
                    <TableCell colSpan={visibleColumns.length} className={`${density.cell} text-center text-muted-foreground py-12`}>
                      {searchTerm || statusFilter !== 'all' || healthFilter !== 'all' ? (
                        <div className="space-y-2">
                          <p className="text-lg font-medium">No projects found</p>
                          <p className="text-sm">Try adjusting your filters or search term</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-lg font-medium">No projects yet</p>
                          <p className="text-sm">Get started by creating your first project</p>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProjects.map(project => (
                    <TableRow 
                      key={project.id} 
                      className={`${density.row} cursor-pointer hover:bg-accent`}
                      onClick={() => {
                        if (onNavigateToProject) {
                          onNavigateToProject(project.id);
                        } else {
                          navigate(ROUTES.PROJECT_DETAILS(project.id));
                        }
                      }}
                    >
                      {visibleColumns.map(column => renderColumnValue(column, project))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {!loading && pagination.totalItems > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              showPageSizeSelector={true}
              className="pt-4 border-t"
            />
          )}
        </CardContent>
      </Card>

      {/* Project Creation Modal */}
      <ProjectCreationModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        mode="create"
        workflowStyle={preferences.projectCreationWorkflow}
        onProjectCreated={loadProjects}
      />

      {/* Column Configurator Drawer */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={handleSaveColumns}
      />
    </div>
  );
}
